import {
  Badge,
  type BadgeTone,
  Image,
  type ItemIdentifier,
  ItemIdentifiers,
  Tooltip,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { ItemStates, Tabs } from '@utility/constants/common';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { sizedImage } from '../../../../lib/listingImage';

export type MarketplaceErrors = {
  marketplaceName: string;
  errors: { propertyName: string; maxLength: number }[];
}[];

type TitledItem = {
  _id: string;
  p_sku?: string;
  p_title?: string;
  short_title?: string;
  secondary_identifier?: string | null;
  state?: string | null;
  is_bundle?: boolean;
  is_duplicate_listing?: boolean;
};

const detailPath = (id: string) =>
  `/app/catalogs/catalog/${encodeURIComponent(id)}`;

function StatusBadge({
  tone,
  children,
}: {
  tone: BadgeTone;
  children: string;
}) {
  return <Badge tone={tone}>{children}</Badge>;
}

/* What still needs fixing on the item leads to the mapping page that fixes it. */
function IssueBadge({ item }: { item: TitledItem }) {
  if (item.state !== ItemStates.conflict) return null;

  const tab = item.is_duplicate_listing
    ? Tabs.catalogConflict.mergeListing
    : Tabs.catalogConflict.resolveConflict;

  return (
    <Link
      to={`/app/catalogs/${tab}/?sku=${encodeURIComponent(item.p_sku ?? '')}`}
      className="sk:no-underline"
    >
      <StatusBadge tone="danger">
        {item.is_duplicate_listing
          ? 'Duplicate listing'
          : 'Missing information'}
      </StatusBadge>
    </Link>
  );
}

function LifecycleBadge({ state }: { state?: string | null }) {
  switch (state) {
    case ItemStates.inactive:
      return <StatusBadge tone="neutral">Inactive</StatusBadge>;
    case ItemStates.discontinued:
      return <StatusBadge tone="neutral">Discontinued</StatusBadge>;
    case ItemStates.component:
      return <StatusBadge tone="info">Component</StatusBadge>;
    default:
      return null;
  }
}

/* Marketplace rules the title or description break, e.g. Walmart's length. */
function MarketplaceErrorBadge({ errors }: { errors: MarketplaceErrors }) {
  if (!errors.length) return null;

  return (
    <Tooltip
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          <span>Some marketplaces need changes to this item:</span>
          {errors.flatMap(marketplace =>
            marketplace.errors.map(error => (
              <span
                key={`${marketplace.marketplaceName}-${error.propertyName}`}
              >
                {CommonUtility.toCapitalize(marketplace.marketplaceName)}:{' '}
                {error.propertyName} is over {error.maxLength} characters.
              </span>
            )),
          )}
        </span>
      }
    >
      <StatusBadge tone="danger">Marketplace error</StatusBadge>
    </Tooltip>
  );
}

/**
 * The item's title and its codes with copy buttons, then its status badges
 * beside them, centred on the row like the order list's flags.
 */
export function ItemTitleCell({
  item,
  marketplaceErrors = [],
  showState = true,
  linkState,
  identifiers = [],
}: {
  item: TitledItem;
  marketplaceErrors?: MarketplaceErrors;
  /** Handed to the item's page, e.g. the rows it can step through. */
  linkState?: unknown;
  /** Off where every row shares the state, e.g. the Components list. */
  showState?: boolean;
  /** Codes after SKU and ASIN, e.g. a provider's UPC; all but the first wait behind a chip. */
  identifiers?: ItemIdentifier[];
}) {
  const title = item.short_title || item.p_title || '';

  return (
    <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
      <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-1">
        <Link
          to={detailPath(item._id)}
          state={linkState}
          className="sk:min-w-0 sk:text-sm sk:text-fg sk:no-underline sk:hover:underline"
        >
          {title}
        </Link>
        <ItemIdentifiers
          itemTitle={title}
          identifiers={[
            { label: 'SKU', value: item.p_sku },
            { label: 'ASIN', value: item.secondary_identifier ?? undefined },
            ...identifiers,
          ]}
        />
      </div>
      <span className="sk:flex sk:shrink-0 sk:flex-wrap sk:items-center sk:gap-1.5 sk:empty:hidden">
        <IssueBadge item={item} />
        {item.is_bundle ? <StatusBadge tone="accent">Kit</StatusBadge> : null}
        {showState ? <LifecycleBadge state={item.state} /> : null}
        <MarketplaceErrorBadge errors={marketplaceErrors} />
      </span>
    </div>
  );
}

/**
 * The product cell of every item table: the item's image and its title cell, both leading to its page, with an
 * optional line under the codes (variant count, storage class) and the row's expand control at the end.
 */
export function ItemProductCell({
  item,
  imageUrl,
  linkState,
  marketplaceErrors,
  showState,
  identifiers,
  children,
  expand,
}: {
  item: TitledItem;
  imageUrl?: string | null;
  linkState?: unknown;
  marketplaceErrors?: MarketplaceErrors;
  showState?: boolean;
  identifiers?: ItemIdentifier[];
  children?: ReactNode;
  expand?: ReactNode;
}) {
  return (
    <div className="sk:flex sk:items-center sk:gap-3">
      <Link
        to={detailPath(item._id)}
        state={linkState}
        className="sk:block sk:shrink-0"
      >
        <Image
          src={sizedImage(imageUrl, 'thumb')}
          alt=""
          size="md"
          fit="contain"
          loading="lazy"
        />
      </Link>
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
        <ItemTitleCell
          item={item}
          marketplaceErrors={marketplaceErrors}
          linkState={linkState}
          showState={showState}
          identifiers={identifiers}
        />
        {children}
      </div>
      {expand}
    </div>
  );
}
